/**
 * Review service — owner review inbox, owner replies, and public review flagging.
 *
 * Backend endpoints:
 *   GET    /reviews/mine            reviews on the signed-in owner's listings
 *   PATCH  /reviews/:id/reply      owner reply to a review
 *   POST   /reviews/:id/flag       report / flag a review (public)
 *
 * Demo data is only used when no API URL is configured (preview mode); with a
 * live API, failures propagate so the portal can show an error + retry state.
 */

import { apiGetPagedAuth, apiPatchAuth, apiPost, isApiConfigured } from "./http";
import { demoOwnerReviews } from "@/mocks/ownerReviews";

export type OwnerReviewStatus = "pending" | "approved" | "rejected";

export interface OwnerReview {
  id:            number;
  businessId:    number;
  businessName:  string;
  businessSlug:  string;
  authorName:    string;
  rating:        number;
  comment:       string | null;
  ownerReply:    string | null;
  repliedAt:     string | null;
  flagCount:     number;
  status:        OwnerReviewStatus;
  reviewedAt:    string | null;
  createdAt:     string;
}

const delay = (ms = 320): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Fetch the reviews left on the signed-in owner's listings. */
export async function getMyReviews(
  token: string | null,
  status = "all",
): Promise<OwnerReview[]> {
  if (isApiConfigured() && token) {
    const qs = status !== "all" ? `&status=${encodeURIComponent(status)}` : "";
    const result = await apiGetPagedAuth<OwnerReview>(`/reviews/mine?limit=50${qs}`, token);
    return result.data;
  }
  await delay();
  return demoOwnerReviews as OwnerReview[];
}

/** Publish an owner reply to a review on one of their listings. */
export async function replyToReview(
  token: string | null,
  id: number,
  reply: string,
): Promise<void> {
  if (isApiConfigured() && token) {
    await apiPatchAuth<{ id: number }>(`/reviews/${id}/reply`, { reply }, token);
    return;
  }
  await delay(500);
}

export interface FlagPayload {
  reason:   string;
  details?: string;
}

/** Report / flag a review — enters the admin moderation queue. */
export async function flagReview(
  reviewId: number | string,
  payload: FlagPayload,
): Promise<void> {
  if (isApiConfigured()) {
    await apiPost<{ id: number }>(`/reviews/${reviewId}/flag`, payload);
    return;
  }
  await delay(360);
}